import {
  CART_SECURITY_CONFIG,
  CartContext,
  CartError,
  CartProvider,
  CartStorage,
  DEFAULT_CART_CONFIG,
  cartReducer,
  createCartSecurityContext,
  createInitialCartState,
  createSecurityAudit,
  filterCartItemsByOwnership,
  formatCurrency,
  generateSecureSessionId,
  generateSecureStorageKey,
  getBackorderedItems,
  getEffectivePrice,
  getPriceDisplayInfo,
  hasBackorderedItems,
  hasMetrcTrackedItems,
  isCartItemBackordered,
  isSameCompanyContext,
  isSecurityContextEqual,
  logSecurityEvent,
  sanitizeSecurityContext,
  useCart,
  useCartAuth,
  useCartDrawer,
  useCartLoading,
  useCartTotals,
  useProductInCart,
  validateCartCompanyIsolation,
  validateCartItemOwnership,
  validateCartStateIntegrity,
  validateCustomerForCart,
  validateSecurityContext
} from "./chunk-OI7HV3EZ.js";
import {
  AuthContext,
  AuthProvider,
  tokenManager,
  useAuth,
  useAuthContext,
  useCreateB2BWaveCustomer,
  useLogout,
  useMe,
  useRefreshToken,
  useSendSmsVerification,
  useVerifyEmail,
  useVerifyOTP,
  useVerifySmsCode,
  verifyCustomerAccount
} from "./chunk-MK6OQIFL.js";
import {
  AnalyticsEvents,
  AnalyticsProvider,
  PostHogAdapter,
  RouteTracker,
  postHogAdapter,
  useAnalytics,
  useAnalyticsContext,
  useAnalyticsSafe,
  useFeatureFlag,
  useFeatureFlagVariant,
  useFunnelTracking,
  useGroupAnalytics
} from "./chunk-FWPRBMZM.js";
import {
  initializeApiClient,
  queryClient,
  rootHttpContract,
  tsr
} from "./chunk-N67WXNDR.js";
import {
  PaymentAnnouncementBanner
} from "./chunk-6BTEVA2A.js";
import {
  ClientLogger,
  LOG_LEVELS,
  configureLogger,
  logger
} from "./chunk-4R6JFI7D.js";
import {
  ModalRouteResetter,
  createModalContext
} from "./chunk-DZTQGHFE.js";

// src/utils/cloudinary.ts
function getCloudinaryTransformedUrl(originalUrl, transformations) {
  if (!originalUrl || !originalUrl.includes("res.cloudinary.com")) {
    return originalUrl || "";
  }
  const parts = originalUrl.split("/upload/");
  if (parts.length !== 2) {
    return originalUrl;
  }
  const transformationString = Object.entries(transformations).map(([key, value]) => {
    const keyMap = {
      width: "w",
      height: "h",
      quality: "q",
      format: "f",
      crop: "c"
    };
    const shortKey = keyMap[key];
    if (shortKey && value !== void 0) {
      return `${shortKey}_${value}`;
    }
    return "";
  }).filter(Boolean).join(",");
  if (!transformationString) {
    return originalUrl;
  }
  return `${parts[0]}/upload/${transformationString}/${parts[1]}`;
}

// src/hooks/useDebounce.ts
import { useState, useEffect } from "react";
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// src/hooks/useGooglePlaces.ts
import { useEffect as useEffect2, useRef, useState as useState2, useCallback } from "react";
var GOOGLE_MAPS_SCRIPT_ID = "google-maps-script";
var DEFAULT_TYPES = ["address"];
var DEFAULT_COMPONENT_RESTRICTIONS = { country: "us" };
function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve();
      return;
    }
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Google Maps")));
      return;
    }
    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
}
function parseAddressComponents(components, formattedAddress) {
  const result = {
    formattedAddress,
    number: "",
    street: "",
    apartment: "",
    city: "",
    state: "",
    postalCode: "",
    country: ""
  };
  for (const component of components) {
    const types = component.types;
    if (types.includes("street_number")) {
      result.number = component.long_name;
    } else if (types.includes("route")) {
      result.street = component.long_name;
    } else if (types.includes("subpremise")) {
      result.apartment = component.long_name;
    } else if (types.includes("locality") || types.includes("sublocality_level_1")) {
      result.city = component.long_name;
    } else if (types.includes("administrative_area_level_1")) {
      result.state = component.short_name;
    } else if (types.includes("postal_code")) {
      result.postalCode = component.long_name;
    } else if (types.includes("country")) {
      result.country = component.short_name;
    }
  }
  return result;
}
function useGooglePlaces(apiKey, options = {}) {
  const {
    onPlaceSelect,
    types = DEFAULT_TYPES,
    componentRestrictions = DEFAULT_COMPONENT_RESTRICTIONS
  } = options;
  const inputElementRef = useRef(null);
  const autocompleteRef = useRef(null);
  const observerRef = useRef(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const [isLoaded, setIsLoaded] = useState2(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState2(false);
  const [error, setError] = useState2(null);
  useEffect2(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);
  useEffect2(() => {
    if (!apiKey) {
      setError("Google Maps API key not configured");
      return;
    }
    let mounted = true;
    loadGoogleMapsScript(apiKey).then(() => {
      if (mounted) {
        setIsScriptLoaded(true);
      }
    }).catch((err) => {
      if (mounted) {
        setError(err.message);
      }
    });
    return () => {
      mounted = false;
    };
  }, [apiKey]);
  const initializeAutocomplete = useCallback((inputElement) => {
    if (!isScriptLoaded || !inputElement) return;
    if (autocompleteRef.current) return;
    autocompleteRef.current = new google.maps.places.Autocomplete(inputElement, {
      types,
      componentRestrictions,
      fields: ["address_components", "place_id", "formatted_address"]
    });
    autocompleteRef.current.addListener("place_changed", () => {
      if (!autocompleteRef.current) return;
      const place = autocompleteRef.current.getPlace();
      if (!place.address_components) return;
      const parsed = parseAddressComponents(
        place.address_components,
        place.formatted_address || ""
      );
      onPlaceSelectRef.current?.(parsed, place.place_id);
    });
    const preventPropagation = (e) => {
      e.stopPropagation();
    };
    const attachPacContainerListeners = () => {
      const pacContainers = document.querySelectorAll(".pac-container");
      pacContainers.forEach((container) => {
        container.addEventListener("click", preventPropagation, true);
        container.addEventListener("mousedown", preventPropagation, true);
        container.addEventListener("pointerdown", preventPropagation, true);
      });
    };
    attachPacContainerListeners();
    observerRef.current = new MutationObserver(() => {
      attachPacContainerListeners();
    });
    observerRef.current.observe(document.body, { childList: true, subtree: true });
    setIsLoaded(true);
  }, [isScriptLoaded, types, componentRestrictions]);
  const cleanupAutocomplete = useCallback(() => {
    if (autocompleteRef.current) {
      google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    setIsLoaded(false);
  }, []);
  const inputRef = useCallback((node) => {
    if (inputElementRef.current && inputElementRef.current !== node) {
      cleanupAutocomplete();
    }
    inputElementRef.current = node;
    if (node && isScriptLoaded) {
      initializeAutocomplete(node);
    }
  }, [isScriptLoaded, initializeAutocomplete, cleanupAutocomplete]);
  useEffect2(() => {
    if (isScriptLoaded && inputElementRef.current && !autocompleteRef.current) {
      initializeAutocomplete(inputElementRef.current);
    }
  }, [isScriptLoaded, initializeAutocomplete]);
  useEffect2(() => {
    return () => {
      cleanupAutocomplete();
    };
  }, [cleanupAutocomplete]);
  return { inputRef, isLoaded, error };
}

// src/hooks/useProductFiles.ts
function useProductFiles(productId) {
  return tsr.products.b2bwaveProductFiles.useQuery({
    queryKey: ["product-files", productId],
    queryData: {
      params: { productId: productId ?? 0 }
    },
    enabled: productId !== null && productId > 0,
    staleTime: 30 * 60 * 1e3
  });
}
export {
  AnalyticsEvents,
  AnalyticsProvider,
  AuthContext,
  AuthProvider,
  CART_SECURITY_CONFIG,
  CartContext,
  CartError,
  CartProvider,
  CartStorage,
  ClientLogger,
  DEFAULT_CART_CONFIG,
  LOG_LEVELS,
  ModalRouteResetter,
  PaymentAnnouncementBanner,
  PostHogAdapter,
  RouteTracker,
  cartReducer,
  configureLogger,
  createCartSecurityContext,
  createInitialCartState,
  createModalContext,
  createSecurityAudit,
  filterCartItemsByOwnership,
  formatCurrency,
  generateSecureSessionId,
  generateSecureStorageKey,
  getBackorderedItems,
  getCloudinaryTransformedUrl,
  getEffectivePrice,
  getPriceDisplayInfo,
  hasBackorderedItems,
  hasMetrcTrackedItems,
  initializeApiClient,
  isCartItemBackordered,
  isSameCompanyContext,
  isSecurityContextEqual,
  logSecurityEvent,
  logger,
  postHogAdapter,
  queryClient,
  rootHttpContract,
  sanitizeSecurityContext,
  tokenManager,
  tsr,
  useAnalytics,
  useAnalyticsContext,
  useAnalyticsSafe,
  useAuth,
  useAuthContext,
  useCart,
  useCartAuth,
  useCartDrawer,
  useCartLoading,
  useCartTotals,
  useCreateB2BWaveCustomer,
  useDebounce,
  useFeatureFlag,
  useFeatureFlagVariant,
  useFunnelTracking,
  useGooglePlaces,
  useGroupAnalytics,
  useLogout,
  useMe,
  useProductFiles,
  useProductInCart,
  useRefreshToken,
  useSendSmsVerification,
  useVerifyEmail,
  useVerifyOTP,
  useVerifySmsCode,
  validateCartCompanyIsolation,
  validateCartItemOwnership,
  validateCartStateIntegrity,
  validateCustomerForCart,
  validateSecurityContext,
  verifyCustomerAccount
};
